import AutorenewIcon from '@mui/icons-material/Autorenew';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { Box, Icon, Stack, Typography } from '@mui/material';
import CustomCard from 'common/components/CustomCard';
import CustomIconButton from 'common/components/CustomIconButton';
import CustomTooltip from 'common/components/CustomTooltip';
import YesNoDialog from 'common/components/YesNoDialog';
import UserRole from 'common/enums/roles';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getUser, testFeatureEnabled } from 'redux/slices/authSlice';
import { closeCustomDialogAction, openCustomDialog } from 'redux/slices/customDialogSlice';
import { deleteServiceAction, getServicesAction, recalculateServiceIssuesAction } from 'redux/slices/iCalendarV4';
import { makeStyles } from 'tss-react/mui';
import { useSettingValue } from '../../../common/hooks/useSettingValue';
import MicroFront from '../../../components/micro-front/MicroFront';
import SettingsKey from '../../profile/settings/utils/settingsTypes';
import AppointmentCreate from '../iCalendar/appointmentForm/AppointmentCreate';
import ServiceShare from './ServiceShare';
import ServiceCapacity from './components/ServiceCapacity';
import ServiceEdit from './serviceForm/ServiceEdit';

const MICRO_FRONT_iCALENDAR_SLUG = 'icalendar';

const useICalendarStyles = makeStyles()((theme) => ({
    mf: {
        width: 860,
        height: 681,
    },
    issueIcon: {
        fontSize: '16px',
        padding: '5px',
        borderRadius: '100%',
        color: '#fff',
        background: 'rgba(237, 108, 2, 0.9)',
        boxShadow: theme.shadows[2],
        pointerEvents: 'auto',
        fontFamily: 'Material Icons',
    },
}));

const ServiceItem = ({ service, type, isService, group, capacity, permissions }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const isTestFeatureEnabled = useSelector(testFeatureEnabled);
    const user = useSelector(getUser);
    const isRoot = user?.role === UserRole.ROOT;

    const calendarVersion = useSettingValue(SettingsKey.CALENDAR_VERSION) ?? 3;

    const navigate = useNavigate();
    const { classes: iCalendarStyles } = useICalendarStyles();

    const [openConfirmDeleteDlg, setOpenConfirmDeleteDlg] = useState(false);
    const [openConfirmRecalculateDlg, setOpenConfirmRecalculateDlg] = useState(false);

    const titleDlg = t('service.serviceItem.deleteConfirm');
    const messageDlg = t('service.serviceItem.deleteMessage');

    const handleDelete = (e) => {
        e.stopPropagation();
        setOpenConfirmDeleteDlg(true);
    };

    const confirmDelete = () => {
        dispatch(
            deleteServiceAction({
                id: service.id,
                onSuccess: () => {
                    enqueueSnackbar(t('videoHelp.videoHelpItem.wasDeleted'), { variant: 'success' });
                },
            }),
        );
        setOpenConfirmDeleteDlg(false);
    };

    const cancelDlg = () => {
        setOpenConfirmDeleteDlg(false);
    };

    const handleRecalculate = (e) => {
        e.stopPropagation();
        setOpenConfirmRecalculateDlg(true);
    };

    const confirmRecalculate = () => {
        dispatch(
            recalculateServiceIssuesAction({
                organizationId: user?.organizationId,
                serviceId: service.id,
                onSuccess: () => {
                    enqueueSnackbar(t('services.recalculateIssues.success'), { variant: 'success' });
                },
                onFailure: () => {
                    enqueueSnackbar(t('app.common.error'), { variant: 'error' });
                },
            }),
        );
        setOpenConfirmRecalculateDlg(false);
    };

    const handleEdit = () => {
        dispatch(
            getServicesAction({
                id: service.id,
                queryParams: { expand: ['calendars', 'availabilities', 'issues'] },
                onSuccess: (data) => dispatch(openCustomDialog(<ServiceEdit service={data} />)),
                onFailure: () => dispatch(openCustomDialog(<ServiceEdit service={service} />)),
            }),
        );
    };

    const getStartDate = (data) => {
        if (!data || data.length === 0) return null;
        let date = data.reduce((min, current) => {
            const temp = new Date(current.startAt);
            return temp < min ? temp : min;
        }, new Date(data[0].startAt));
        if (Number.isNaN(date?.getTime())) {
            date = new Date();
        }
        return date?.toISOString();
    };

    const getSubtitle = (service) => {
        const subtitles = [];
        const startDate = getStartDate(service?.dateRanges);
        if (startDate) {
            subtitles.push(`${t('common.from')}: ${moment(startDate).format('DD/MM/YYYY HH:mm')}`);
        }

        if (group) {
            subtitles.push(`${t('services.form.group')}: ${group.name}`);
        }
        if (type) {
            subtitles.push(`${t('app.common.type')}: ${type.name}`);
        }
        return subtitles;
    };

    const BodyText = ({ text }) => {
        return (
            <Typography variant="body1" gutterBottom component="div">
                {text}
            </Typography>
        );
    };

    const cardBody = (service) => {
        return (
            <Stack direction="column" spacing={1}>
                <BodyText
                    text={`${service.bookingOptions?.slotIntervalMinutes} ${t(
                        'iCalendar.schedule.minutesBetweenEvents',
                    )}`}
                />
                <BodyText
                    text={`${service.bookingOptions?.bookingDurationMinutes} ${t(
                        'iCalendar.schedule.minutesPerEvents',
                    )}`}
                />
                <BodyText
                    text={`${(service.bookingOptions?.minAdvanceBookingMinutes ?? 0) / 60 / 24} ${t(
                        'iCalendar.schedule.minTimeToSchedule',
                    )}`}
                />
                <BodyText
                    text={`${(service.bookingOptions?.maxAdvanceBookingMinutes ?? 0) / 60 / 24} ${t(
                        'iCalendar.schedule.maxTimeToSchedule',
                    )}`}
                />

                {capacity && (
                    <Box sx={{ my: 1 }}>
                        <ServiceCapacity capacity={capacity} />
                    </Box>
                )}
            </Stack>
        );
    };

    const openShare = () => {
        dispatch(openCustomDialog(<ServiceShare service={service} group={group} />));
    };

    const actions = (service) => {
        return (
            <>
                {isRoot && (
                    <CustomTooltip
                        key={`action-recalculate-${service.id}`}
                        arrow
                        placement="bottom"
                        title={t('services.recalculateIssues.action')}
                        aria-label="recalculate service issues"
                    >
                        <CustomIconButton id="onlive-button-recalculate" size="small" onClick={handleRecalculate}>
                            <AutorenewIcon component="svg" />
                        </CustomIconButton>
                    </CustomTooltip>
                )}

                {permissions.write && (
                    <CustomTooltip
                        key={`action-edit-${service.id}`}
                        arrow
                        placement="bottom"
                        title={t('actions.edit')}
                        aria-label="edit service"
                    >
                        <CustomIconButton id="onlive-button-edit" size="small" onClick={handleEdit}>
                            <EditOutlinedIcon component="svg" />
                        </CustomIconButton>
                    </CustomTooltip>
                )}

                <CustomTooltip
                    key={`action-schedule-${service.id}`}
                    arrow
                    placement="bottom"
                    title={t('actions.schedule')}
                    aria-label="schedule"
                >
                    <CustomIconButton
                        id="onlive-button-schedule"
                        size="small"
                        onClick={() => {
                            if (Number(calendarVersion) === 4) {
                                dispatch(
                                    openCustomDialog(MicroFront, {
                                        contentProperties: {
                                            slug: MICRO_FRONT_iCALENDAR_SLUG,
                                            path: 'schedule',
                                            data: { service },
                                            onDialogClose: (appointment) => {
                                                dispatch(closeCustomDialogAction());
                                                if (appointment) {
                                                    enqueueSnackbar(t('service.serviceItem.scheduled'), {
                                                        variant: 'success',
                                                    });
                                                    navigate(
                                                        `/i-calendar?view=day&date=${moment(appointment.startAt).format(
                                                            'YYYY-MM-DD',
                                                        )}`,
                                                    );
                                                }
                                            },
                                            className: iCalendarStyles.mf,
                                        },
                                        showCloseIcon: false,
                                    }),
                                );
                            } else {
                                dispatch(openCustomDialog(<AppointmentCreate service={service} />));
                            }
                        }}
                    >
                        <DateRangeOutlinedIcon component="svg" />
                    </CustomIconButton>
                </CustomTooltip>

                <CustomTooltip
                    key={`action-share-${service.id}`}
                    arrow
                    placement="bottom"
                    title={t('actions.share')}
                    aria-label="share"
                >
                    <CustomIconButton aria-label="copy to share" size="small" onClick={openShare}>
                        <ShareOutlinedIcon />
                    </CustomIconButton>
                </CustomTooltip>

                {isService && permissions.write ? (
                    <CustomTooltip
                        key={`action-delete-${service.id}`}
                        arrow
                        placement={'bottom'}
                        title={t('app.common.delete')}
                        aria-label="delete"
                    >
                        <CustomIconButton size="small" id="onlive-button-delete" onClick={handleDelete}>
                            <DeleteOutlinedIcon component="svg" />
                        </CustomIconButton>
                    </CustomTooltip>
                ) : null}
            </>
        );
    };

    const issuesOverlay = () => {
        const issues = service?.issues || [];
        if (!issues.length || !isTestFeatureEnabled) {
            return null;
        }
        return issues.map((issue, idx) => (
            <CustomTooltip key={issue.code || idx} arrow placement="top" title={issue.title || issue.code}>
                <Icon className={iCalendarStyles.issueIcon}>{issue.icon || 'warning'}</Icon>
            </CustomTooltip>
        ));
    };

    if (!service) return null;

    return (
        <>
            <CustomCard
                title={service.name}
                subtitles={getSubtitle(service)}
                image={service.mainImage || '/broken-image.jpg'}
                imageOverlay={issuesOverlay()}
                titleDivider={true}
                actions={actions(service)}
                cardBody={cardBody(service)}
                actionsAlign="right"
                borderRadius={2}
            />
            {openConfirmDeleteDlg && (
                <YesNoDialog
                    openDialog={true}
                    title={titleDlg}
                    messageToConfirm={messageDlg}
                    yesButtonCallback={() => {
                        confirmDelete();
                    }}
                    noButtonCallback={() => {
                        cancelDlg();
                    }}
                />
            )}
            {openConfirmRecalculateDlg && (
                <YesNoDialog
                    openDialog={true}
                    title={t('services.recalculateIssues.confirmTitle')}
                    messageToConfirm={t('services.recalculateIssues.confirmMessageService')}
                    yesButtonText={t('services.recalculateIssues.action')}
                    yesButtonCallback={confirmRecalculate}
                    noButtonCallback={() => setOpenConfirmRecalculateDlg(false)}
                />
            )}
        </>
    );
};

export default ServiceItem;
